import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useCallback } from 'react';

import { useOptimisticState } from '@/hooks/useOptimisticState';

import type { IGCommentWatcherClassContent } from '../../../../bridge/types/artifact.ts';

import { AvatarCircle } from './AvatarCircle';
import {
	CollapsiblePrompt,
	MutedLabel,
} from './comment-watcher/CollapsiblePrompt';
import type { ArtifactComponentProps } from './types';

type CommentMonitorConditionProps =
	ArtifactComponentProps<IGCommentWatcherClassContent>;

const exampleBubbleBackground = style({
	backgroundColor: 'informative-200',
});

export const CommentMonitorCondition = memo(function CommentMonitorCondition({
	content,
	isReadOnly = false,
	onContentChange,
}: CommentMonitorConditionProps) {
	const [conditionValue, setConditionValue] = useOptimisticState(
		content.condition,
	);

	const handleConditionCommit = useCallback(
		(value: string) => onContentChange?.({ condition: value }),
		[onContentChange],
	);

	return (
		<div className="flex flex-col gap-2.5">
			<div>
				<MutedLabel>Prompt</MutedLabel>
				<div className="mt-1">
					<CollapsiblePrompt
						value={conditionValue}
						isReadOnly={isReadOnly}
						onChange={setConditionValue}
						onCommit={(value) => {
							if (value !== content.condition) {
								handleConditionCommit(value);
							}
						}}
					/>
				</div>
			</div>

			{content.example_comment && (
				<div className="py-1">
					<MutedLabel>Example comment</MutedLabel>
					<div className="mt-1.5 flex items-start gap-2">
						<div className="mt-px shrink-0">
							<AvatarCircle username="instagram_user" />
						</div>
						<div className="min-w-0">
							<span
								className="block text-[0.6875rem] leading-[1.3] font-semibold
									text-(--text-primary)"
							>
								instagram_user
							</span>
							<div
								className={`${exampleBubbleBackground} mt-[3px] inline-block
								max-w-full rounded-[0_8px_8px_8px] px-[9px] py-1`}
							>
								<p className="m-0 text-xs leading-[1.4] text-(--text-secondary)">
									{content.example_comment}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
});
